/** @format */

import React, { FC, useState, useEffect, useRef, useMemo } from 'react'
import { _$, Icon, FormItem, Table } from 'kpc-react'
import styled from 'styled-components'
import { useAsyncFn } from 'react-use'
import { getTemplateList } from 'src/api/ApiAlarmTemplate'
import { Select, Option } from '@king-design/react'
import AlarmRule, { AlarmRuleHandle } from 'src/containers/MonitorCreate/AlarmRule'
import { handleParamsCase } from 'src/utils/common'
import { handleMetricUnits } from '../MonitorCreate/AlarmRule/constants'

interface SelectTemplateProps {
  className?: string
  productType: number
  type: 'threshold' | 'event'
  value?: number
  eventList?: any[]
  onChange: (data: any) => void
}

const SelectTemplate: FC<SelectTemplateProps> = ({
  className,
  productType,
  value,
  type,
  eventList = [],
  onChange
}) => {
  const rulesRef = useRef<AlarmRuleHandle>()
  const [templateID, setTemplateID] = useState<number>(value)

  useEffect(() => {
    setTemplateID(value)
  }, [value])

  const [{ value: templates, loading }, templates_fetch] = useAsyncFn(async () => {
    if (productType !== undefined && productType !== null) {
      const result = await getTemplateList({
        ProductType: productType,
        PageIndex: 1,
        PageSize: 9999
      })
      const filter = result?.filter(({ EventPattern, TriggerRules }) => {
        if (type === 'threshold') {
          return TriggerRules?.length
        } else if (type === 'event') {
          return EventPattern?.EventNames?.length
        } else {
          return false
        }
      })
      if (!filter?.length) {
        onChange && onChange(undefined)
      }
      return filter
    } else {
      return []
    }
  }, [productType])

  useEffect(() => {
    templates_fetch()
  }, [productType])

  const selectedTemplate = useMemo(() => {
    if (templates?.length) {
      if (templateID) {
        const find = templates.find(template => template.TemplateID === templateID) || []
        const template = handleParamsCase(find, 'lower')
        onChange && onChange(find)
        return template
      } else {
        return {}
      }
    } else {
      return {}
    }
  }, [templates, templateID])

  // 已选事件Table
  const selectedScheme = {
    nameDesc: {
      title: _$('事件名称'),
      width: 320
    },
    eventLevel: {
      title: _$('事件等级'),
      width: 120
    },
    eventTypeDesc: {
      title: _$('事件类型'),
      width: 320
    }
  }

  return (
    <div className={className}>
      <div className="select_template">
        <FormItem
          label={_$('条件触发模版')}
          value={templateID}
          rules={{ required: true }}
          messages={{
            required: _$('请选择条件触发模版')
          }}
        >
          <Select
            value={templateID}
            clearable
            loading={loading}
            onChangeValue={value => {
              setTemplateID(value)
            }}
            filterable
          >
            {templates?.map(({ TemplateName, TemplateID }) => (
              <Option key={TemplateID} value={TemplateID}>
                {TemplateName}
              </Option>
            ))}
          </Select>
        </FormItem>
        <Icon
          className="ion-refresh cursor-pointer"
          onClick={() => {
            templates_fetch()
          }}
        />
        <a
          onClick={() => {
            window.open(`/#!/monitor/alarmServer/template/edit`)
          }}
        >
          <Icon className="ion-plus-round"></Icon>
          {_$('新建模板')}
        </a>
        {templateID ? (
          <a
            onClick={() => {
              window.open(`/#!/monitor/alarmServer/template/edit/${templateID}`)
            }}
          >
            <Icon className="ion-edit"></Icon>
            {_$('修改模板')}
          </a>
        ) : null}
      </div>
      {type === 'threshold' ? (
        <div className="threshold_select">
          <AlarmRule
            ref={rulesRef}
            className="alarm_item"
            readonly={true}
            productType={productType}
            triggerRules={selectedTemplate?.triggerRules?.map(item => {
              const { itemKey, units, triggerValue, displayUnits } = item
              const value = handleMetricUnits(
                productType,
                itemKey,
                triggerValue,
                units,
                displayUnits
              )
              return {
                ...item,
                triggerValue: value + ''
              }
            })}
          />
        </div>
      ) : null}
      {type === 'event' ? (
        <div className="event_select">
          {selectedTemplate?.eventPattern?.eventNames?.[0] === '*' ? <p>全部事件</p> : null}
          {selectedTemplate?.eventPattern?.eventNames?.length &&
          selectedTemplate?.eventPattern?.eventNames?.[0] !== '*' ? (
            <Table
              scheme={selectedScheme}
              checkType="none"
              data={
                eventList?.length
                  ? selectedTemplate?.eventPattern?.eventNames?.map(name => {
                      const find = eventList.find(
                        item => item.productType === productType && item.name === name
                      )
                      return {
                        id: name,
                        nameDesc: find?.nameDesc,
                        eventLevel: find?.eventLevel,
                        eventTypeDesc: find?.eventTypeDesc
                      }
                    })
                  : []
              }
            ></Table>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export default styled(SelectTemplate)`
  .k-select {
    display: inline-flex;
  }
  .select_template {
    display: flex;
    align-items: center;
    & > * {
      margin-right: 8px;
    }
    & > a {
      vertical-align: middle;
      .k-icon {
        margin-right: 4px;
        font-size: 14px;
      }
    }
  }
  .threshold_select {
    margin-top: 32px;
  }
  .event_select {
    margin-top: 16px;
    padding-bottom: 16px;
    padding-left: 100px;
  }
`
